import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Building,
    ArrowLeft,
    Edit,
    Users,
    Globe,
    DollarSign,
    Calendar,
    CheckCircle,
    XCircle,
    Clock,
    Mail,
    MapPin,
    ExternalLink,
    Plus,
    Loader2,
    AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { merchantService } from '@/services/merchantService'
import { Merchant } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'
import { useAuthStore } from '@/stores/authStore'

export default function MerchantDetails() {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const [merchant, setMerchant] = useState<Merchant | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (user?.merchant) {
            fetchMerchantData()
        } else {
            setError('No merchant associated with this user')
            setLoading(false)
        }
    }, [user])

    const fetchMerchantData = async () => {
        if (!user?.merchant) {
            setError('No merchant ID found for this user')
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            setError(null)

            // Fetch merchant details and users
            const [merchantResponse, _usersResponse] = await Promise.all([
                merchantService.getMyMerchant(user.merchant),
                merchantService.getMyMerchantUsers(user.merchant)
            ])

            if (merchantResponse.success) {
                setMerchant(merchantResponse.data)
            } else {
                setError('Failed to fetch merchant details')
            }
        } catch (err) {
            setError('An error occurred while fetching merchant data')
            console.error('Error fetching merchant data:', err)
        } finally {
            setLoading(false)
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'enabled':
                return 'text-green-600 bg-green-50 border-green-200'
            case 'disabled':
                return 'text-gray-600 bg-gray-50 border-gray-200'
            case 'pending':
                return 'text-yellow-600 bg-yellow-50 border-yellow-200'
            case 'suspended':
                return 'text-red-600 bg-red-50 border-red-200'
            default:
                return 'text-gray-600 bg-gray-50 border-gray-200'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'enabled':
                return CheckCircle
            case 'disabled':
                return XCircle
            case 'pending':
                return Clock
            case 'suspended':
                return XCircle
            default:
                return Clock
        }
    }

    const getEnvironmentColor = (environment: string) => {
        switch (environment) {
            case 'production':
                return 'text-red-600 bg-red-50 border-red-200'
            case 'sandbox':
                return 'text-blue-600 bg-blue-50 border-blue-200'
            default:
                return 'text-gray-600 bg-gray-50 border-gray-200'
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex items-center space-x-2">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>Loading merchant details...</span>
                </div>
            </div>
        )
    }

    if (error || !merchant) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading merchant</h3>
                    <p className="mt-1 text-sm text-gray-500">{error}</p>
                    <div className="mt-6">
                        <Button onClick={() => navigate('/dashboard')}>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Dashboard
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    const StatusIcon = getStatusIcon(merchant.status || 'pending')

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate('/dashboard')}
                                className="hover:bg-gray-100"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Merchant Details</h1>
                                <p className="text-gray-600 mt-1">Manage your merchant account</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/merchant/users/create')}
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add User
                            </Button>
                            <Button
                                onClick={() => navigate('/merchant/details/edit')}
                                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                            >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Details
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Merchant Information */}
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-gray-900">Merchant Information</h2>
                                <div className="flex items-center space-x-2">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(merchant.status || 'pending')}`}>
                                        <StatusIcon className="h-3 w-3 mr-1" />
                                        {merchant.status ? merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1) : 'Pending'}
                                    </span>
                                    {merchant.environment && (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getEnvironmentColor(merchant.environment)}`}>
                                            {merchant.environment.charAt(0).toUpperCase() + merchant.environment.slice(1)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Company Name</label>
                                    <p className="mt-1 text-sm text-gray-900">{merchant.company_name}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Contact Person</label>
                                    <p className="mt-1 text-sm text-gray-900">{merchant.name}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Email</label>
                                    <div className="mt-1 flex items-center">
                                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                                        <p className="text-sm text-gray-900">{merchant.email}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Currency</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {merchant.currency ? `${merchant.currency.code} - ${merchant.currency.name}` : 'Not specified'}
                                    </p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Country</label>
                                    <div className="mt-1 flex items-center">
                                        <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                                        <p className="text-sm text-gray-900">
                                            {merchant.country ? merchant.country.name : 'Not specified'}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Commission Rate</label>
                                    <div className="mt-1 flex items-center">
                                        <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                                        <p className="text-sm text-gray-900">{merchant.commission_rate}%</p>
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-sm font-medium text-gray-500">Description</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {merchant.description || 'No description provided'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* URLs and Settings */}
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-6">URLs & Settings</h2>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Website URL</label>
                                    <div className="mt-1 flex items-center">
                                        <Globe className="h-4 w-4 text-gray-400 mr-2" />
                                        {merchant.website_url ? (
                                            <a
                                                href={merchant.website_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                            >
                                                {merchant.website_url}
                                                <ExternalLink className="h-3 w-3 ml-1" />
                                            </a>
                                        ) : (
                                            <p className="text-sm text-gray-500">Not specified</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Success Redirect URL</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {merchant.success_redirect_url || 'Not specified'}
                                    </p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Cancel Redirect URL</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {merchant.cancel_redirect_url || 'Not specified'}
                                    </p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Webhook URL</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {merchant.webhook_url || 'Not specified'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Quick Stats */}
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <Users className="h-5 w-5 text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-600">Total Users</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">{merchant.users_count}</span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <Building className="h-5 w-5 text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-600">Submerchants</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">{merchant.submerchants_count}</span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-600">Commission Rate</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">{merchant.commission_rate}%</span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-600">Created</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">
                                        {safeFormatDistanceToNow(merchant.created_at, 'Unknown', { addSuffix: true })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Account Status */}
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h3>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Verification</span>
                                    <div className="flex items-center">
                                        {merchant.is_verified ? (
                                            <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-red-500 mr-1" />
                                        )}
                                        <span className={`text-sm font-medium ${merchant.is_verified ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                            {merchant.is_verified ? 'Verified' : 'Not Verified'}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Status</span>
                                    <span className={`text-sm font-medium ${merchant.status === 'enabled' ? 'text-green-600' :
                                        merchant.status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                                        }`}>
                                        {merchant.status ? merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1) : 'Pending'}
                                    </span>
                                </div>

                                {merchant.environment && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Environment</span>
                                        <span className={`text-sm font-medium ${merchant.environment === 'production' ? 'text-red-600' : 'text-blue-600'
                                            }`}>
                                            {merchant.environment.charAt(0).toUpperCase() + merchant.environment.slice(1)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
